import {Box, Center, Loader, Pagination, Stack, Text} from '@mantine/core';
import React, {useEffect} from 'react';
import type {PillProps} from 'src/design/atoms/Pill';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {isValidArray} from 'src/utilities/objectUtils';
import {DataListRow} from './DataListRow';

/**
 * Paginated, loading-aware list shell: renders `DataListRow` rows with a
 * Mantine `Pagination` control above/below and an empty-state fallback.
 * @registry Paginated list shell with loading/empty states around DataListRow.
 *
 * Ported from `library/tables/DataList`. Prop surface kept identical.
 */

export interface DataListProps {
  data: any;
  meta: any;
  currentPage: number;
  totalRecords: number;
  recordsPerPage?: number;
  isLoading: boolean;
  noDataText?: string;
  rowIcon?: React.ReactNode;
  onRowClick: (id: number | string, item: any) => void;
  /** Rows this returns true for render inert: no click target, no chevron. */
  onIsRowDisabled?: (item: any) => boolean;
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onGetPillMapping?: (item: any) => PillProps[];
  onGetRowIcon?: (item: any) => React.ReactNode;
  onRowTextRender?: (data: any, meta: any) => string;
  onRowSubTextRender?: (data: any, meta: any) => string;
  onRenderBeforeRowNode?: (item: any) => React.ReactNode;
  onRenderAfterRowNode?: (item: any) => React.ReactNode;
  onRenderDetailRowNode?: (data: any) => React.ReactNode;

  hasSearchTerm?: boolean;
  hasCreationLogic?: boolean;
  paginatorAtBottom?: boolean;
}

export const DataList = ({
  data = [],
  meta,
  currentPage,
  totalRecords,
  recordsPerPage = 10,
  noDataText,
  rowIcon,
  onRowClick,
  onIsRowDisabled,
  onPageChange,
  onGetPillMapping,
  onGetRowIcon,
  onRowTextRender,
  onRowSubTextRender,
  onRenderBeforeRowNode,
  onRenderAfterRowNode,
  onRenderDetailRowNode,
  isLoading,
  hasSearchTerm,
  hasCreationLogic,
  paginatorAtBottom = false,
}: DataListProps) => {
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const showPagination = totalPages > 1;
  const lastPage = Math.max(totalPages, 1);

  // A restored or stale page past the end would strand the user on an empty page with no paginator.
  useEffect(() => {
    if (!isLoading && currentPage > lastPage) {
      onPageChange?.({} as React.ChangeEvent<unknown>, lastPage);
    }
  }, [isLoading, currentPage, lastPage, onPageChange]);

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange({} as React.ChangeEvent<unknown>, page);
    }
  };

  const renderPagination = () => {
    if (!showPagination) return null;

    return (
      <Stack gap="xs" mb="xs">
        <Pagination
          total={totalPages}
          value={currentPage}
          onChange={handlePageChange}
          size="sm"
        />
        <Text size="xs" c="dimmed">
          Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
          {Math.min(currentPage * recordsPerPage, totalRecords)} of{' '}
          {totalRecords}
        </Text>
      </Stack>
    );
  };

  return (
    <Box w="100%">
      {!paginatorAtBottom && <Box mb="sm">{renderPagination()}</Box>}

      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <DataListRow
          data={data}
          meta={meta}
          rowIcon={rowIcon}
          hasSearchTerm={hasSearchTerm}
          onRowClick={onRowClick}
          onIsRowDisabled={onIsRowDisabled}
          onGetPillMapping={onGetPillMapping}
          onGetRowIcon={onGetRowIcon}
          onRowTextRender={onRowTextRender}
          onRowSubTextRender={onRowSubTextRender}
          onRenderBeforeRowNode={onRenderBeforeRowNode}
          onRenderAfterRowNode={onRenderAfterRowNode}
          onRenderDetailRowNode={onRenderDetailRowNode}
        />
      )}

      {paginatorAtBottom && <Box mt="sm">{renderPagination()}</Box>}

      {data.length === 0 &&
        !hasSearchTerm &&
        !isValidArray(data) &&
        !isLoading && (
          <NoItemsPanel noItemsText={noDataText} canCreate={hasCreationLogic} />
        )}
    </Box>
  );
};
